import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../../styles/colors';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaRegBuilding } from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ContactSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #f9f9f9 0%, #fff 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${colors.primary}40, transparent);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: ${colors.primary};
  text-align: center;
  margin-bottom: 2rem;
  animation: ${fadeIn} 1s ease-out;
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: ${colors.accent};
    margin: 20px auto 0;
  }
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 40px;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormContainer = styled.div`
  background: ${colors.white};
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 1s ease-out 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  position: relative;
  transition: all 0.3s ease;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  color: ${colors.text};
  transition: all 0.3s ease;
  background: #f8f9fa;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    background: ${colors.white};
    box-shadow: 0 0 0 3px ${colors.primary}20;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  color: ${colors.text};
  transition: all 0.3s ease;
  background: #f8f9fa;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    background: ${colors.white};
    box-shadow: 0 0 0 3px ${colors.primary}20;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  color: ${colors.text};
  transition: all 0.3s ease;
  background: #f8f9fa;
  resize: vertical;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    background: ${colors.white};
    box-shadow: 0 0 0 3px ${colors.primary}20;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background: ${colors.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;

  &:hover {
    background: ${colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoCard = styled.div`
  background: ${colors.white};
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  animation: ${slideIn} 0.8s ease-out ${props => props.delay}s;
  opacity: 0;
  animation-fill-mode: forwards;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const InfoTitle = styled.h3`
  color: ${colors.primary};
  font-size: 1.4rem;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: ${colors.accent};
  }
`;

const InfoText = styled.p`
  color: ${colors.text};
  margin-bottom: 10px;
  line-height: 1.6;

  a {
    color: ${colors.primary};
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: ${colors.accent};
    }
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 20px 0;

  input {
    margin-top: 4px;
  }

  label {
    font-size: 0.9rem;
    line-height: 1.4;
    color: ${colors.text};
  }

  a {
    color: ${colors.primary};
    text-decoration: underline;
    
    &:hover {
      color: ${colors.accent};
    }
  }
`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    unitType: '',
    message: '',
    consent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const formBody = new FormData();
    for (const key in formData) {
      formBody.append(key, formData[key]);
    }

    // POST to PHP on server
    const response = await fetch('/sendMail.php', {
      method: 'POST',
      body: formBody
    });

    const result = await response.json();

    if (result.status === 'success') {
      alert('Form submitted successfully!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        unitType: '',
        consent: false
      });
    } else {
      alert('Failed to submit. Please try again.');
    }
  } catch (err) {
    console.error(err);
    alert('Something went wrong.');
  }
};


  return (
    <ContactSection id="contact">
      <Container>
        <Title>Get in Touch</Title>
        
        <ContactContent>
          <FormContainer>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name*"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email*"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone Number*"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Select
                  name="unitType"
                  value={formData.unitType}
                  onChange={handleChange}
                >
                  <option value="">Select Unit Type</option>
                  <option value="3BHK">3 BHK</option>
                  <option value="4BHK">4 BHK</option>
                  <option value="4BHK">5 BHK</option>
                </Select>
              </FormGroup>
              


              <Checkbox>
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                />
                <label>
                  I hereby consent to the processing of my data in accordance with the 
                  <a href="https://balrajeshwar.com/privacy-policy/" target="_blank" rel="noopener noreferrer"> Privacy Policy </a> 
                  and 
                  <a href="https://balrajeshwar.com/tos/" target="_blank" rel="noopener noreferrer"> Terms & Conditions</a>, 
                  and authorize Balrajeshwar Real Estate Pvt. Ltd. and its representatives to contact me via call, SMS, email, 
                  or WhatsApp regarding their products and offers. This consent overrides any existing DNC/NDNC registrations.
                </label>
              </Checkbox>
              
              <SubmitButton type="submit">
                Request a Callback
              </SubmitButton>
            </Form>
          </FormContainer>
          
          <ContactInfo>
            <InfoCard delay={0.4}>
              <InfoTitle>
                <FaRegBuilding /> Contact Details
              </InfoTitle>
              <InfoText>
                <strong>Phone:</strong> <a href="tel:+919870004341">+91 98700 04341</a>
              </InfoText>
              <InfoText>
                <strong>Email:</strong> <a href="mailto:sales@forresta.in">sales@forresta.in</a>
              </InfoText>
              <InfoText>
                <strong>MahaRERA Regd. Number:</strong> <a href="https://maharerait.maharashtra.gov.in/public/project/view/35250">P51800034630</a>
              </InfoText>
            </InfoCard>
            
            <InfoCard delay={0.6}>
              <InfoTitle>
                <FaMapMarkerAlt /> Sales Office
              </InfoTitle>
              <InfoText>
                The Orchid, Whispering Meadows,<br />
                Agarwal Rd, Opp. Model Town,<br />
                Ghati Pada, Mulund West,<br />
                Mumbai, Maharashtra - 400080
              </InfoText>
            </InfoCard>
          </ContactInfo>
        </ContactContent>
      </Container>
    </ContactSection>
  );
};

export default ContactForm; 